import { randomString } from '@/lib/random'
import { cn } from '@/lib/utils'
import { useAtomValue } from 'jotai'
import { autoLoadMediaAtom } from '@/atoms/preferences'
import { registerModal, unregisterModal } from '@/services/modal-manager.service'
import { TImetaInfo } from '@/types'
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import Lightbox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Image from './Image'

export default function ImageWithLightbox({
  image,
  className,
  classNames = {}
}: {
  image: string | TImetaInfo
  className?: string
  classNames?: {
    wrapper?: string
  }
}) {
  const id = useMemo(() => `image-with-lightbox-${randomString()}`, [])
  const { t } = useTranslation()
  const autoLoadMedia = useAtomValue(autoLoadMediaAtom)
  const [display, setDisplay] = useState(autoLoadMedia)
  const [index, setIndex] = useState(-1)
  const [lightboxSrc, setLightboxSrc] = useState<string | undefined>(
    typeof image === 'string' ? image : image.url
  )

  useEffect(() => {
    if (index >= 0) {
      registerModal(id, () => {
        setIndex(-1)
      })
    } else {
      unregisterModal(id)
    }
  }, [index])

  if (!display) {
    return (
      <div
        className="text-primary hover:underline truncate w-fit cursor-pointer"
        onClick={(e) => {
          e.stopPropagation()
          setDisplay(true)
        }}
      >
        [{t('Click to load image')}]
      </div>
    )
  }

  const [url, pubkey, imeta] =
    typeof image === 'string' ? [image, undefined, undefined] : [image.url, image.pubkey, image]

  return (
    <div>
      <Image
        imageUrl={url}
        imagePubkey={pubkey}
        imeta={imeta}
        className={className}
        classNames={{
          wrapper: cn('rounded-lg border cursor-zoom-in', classNames.wrapper),
          placeholder: 'aspect-square h-[30vh]'
        }}
        showUrl
        imgproxyModifiers="rs:fit:800:800"
        onClick={(e) => handlePhotoClick(e)}
        onChosenUrl={setLightboxSrc}
      />
      {index >= 0 &&
        lightboxSrc &&
        createPortal(
          <div onClick={(e) => e.stopPropagation()}>
            <Lightbox
              index={index}
              slides={[{ src: lightboxSrc }]}
              plugins={[Zoom]}
              open={index >= 0}
              close={() => setIndex(-1)}
              controller={{
                closeOnBackdropClick: true,
                closeOnPullUp: true,
                closeOnPullDown: true
              }}
              carousel={{
                finite: true
              }}
              render={{
                buttonPrev: () => null,
                buttonNext: () => null
              }}
              styles={{
                toolbar: { paddingTop: '2.25rem' }
              }}
            />
          </div>,
          document.body
        )}
    </div>
  )

  function handlePhotoClick(event: React.MouseEvent) {
    event.stopPropagation()
    event.preventDefault()
    setIndex(0)
  }
}
